import React, { useState, useEffect } from "react";
import { Modal, Table, Tag, Button, Tooltip } from "antd";
import { EditOutlined } from "@ant-design/icons";
import moment from "moment";
import { getApi } from "redux/apis";
import { toastText } from "utils/utils";
import styles from "./index.module.scss";

const { Column } = Table;
interface Props {
  visible: boolean;
  onClose: () => void;
  record: any;
  onEdit: (item: any) => void;
  isAdmin: boolean;
  isEmployee: boolean;
  fetchInventory: () => void;
  statusUpdated?: boolean;
  statusstateSetter?: (data: boolean) => void;
  itemStatus?: string;
  selectedServiceCenter: Array<any>;
}

const InventoryDetailsModal: React.FC<Props> = ({
  isAdmin,
  isEmployee,
  visible,
  onClose,
  record,
  onEdit,
  fetchInventory,
  statusUpdated,
  statusstateSetter,
  itemStatus,
  selectedServiceCenter,
}) => {
  console.log("Aaa", selectedServiceCenter);
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [canFetchMore, setCanFetchMore] = useState(true);
  const [sorter, setSorter] = useState<{
    columnKey?: string;
    order?: "ascend" | "descend";
  }>({});
  const [serviceCenter, setServiceCenter] = useState<any>(null);

  // Function to get appropriate color for each status
  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case "RECEIVED":
        return "green";
      case "IN_TRANSIT":
      case "IN TRANSIT":
        return "orange";
      case "SHIPPED":
        return "blue";
      case "PARTIAL":
        return "orange";
      case "DAMAGED":
        return "red";
      case "LOST":
        return "yellow";
      case "PENDING":
        return "gold";
      case "CANCELLED":
      case "CANCELED":
        return "red";
      case "DELIVERED":
        return "cyan";
      default:
        return "default";
    }
  };

  // whenever we open on a new record, reset everything
  useEffect(() => {
    if (visible && record) {
      setData([]);
      setPage(1);
      setCanFetchMore(true);
      setSorter({});
      setServiceCenter(null); // Reset service center when opening a new record
      fetchPage(1, {}, true);
    }
  }, [visible, record, statusUpdated]);

  const fetchPage = async (
    pageNumber: number,
    sorterParams: any,
    reset = false
  ) => {
    setLoading(true);
    try {
      record.status = itemStatus || record.status;
      const query: any = {
        page: pageNumber,
        pageSize: 10,
        serviceCenterId: selectedServiceCenter,
        salesOrderNumber: [record.salesOrderNumber],
        itemNumber: [record.itemNumber],
        status: record.status,
      };
      if (sorterParams.columnKey && sorterParams.order) {
        query.sortBy = sorterParams.columnKey;
        query.sortOrder = sorterParams.order === "ascend" ? "asc" : "desc";
      }
      if (selectedServiceCenter.length > 0) {
        query.serviceCenterId = selectedServiceCenter;
      }
      const resp = await getApi("/inventory", query);

      // Set service center information if available
      if (resp.data.data.uniqueServiceCenter) {
        setServiceCenter(resp.data.data.uniqueServiceCenter);
      }
      const newRows = resp.data.data.data;
      const total = resp.data.data.total || 0;

      setCanFetchMore(pageNumber * 10 < total);
      setData((prev) => (reset ? newRows : [...prev, ...newRows]));
      setPage(pageNumber + 1);
    } catch (err: any) {
      toastText(
        err.response?.data?.error?.message || "Failed to load details",
        "error"
      );
      setCanFetchMore(false);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (_: any, __: any, sorterParams: any) => {
    setSorter({
      columnKey: sorterParams.columnKey,
      order: sorterParams.order,
    });
    fetchPage(1, sorterParams, true);
  };

  const handleScroll = (e: any) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (
      scrollHeight - scrollTop - clientHeight < 1 &&
      !loading &&
      canFetchMore
    ) {
      fetchPage(page, sorter, false);
    }
  };

  return (
    <Modal
      open={visible}
      title={
        loading
          ? "Loading details..."
          : (() => {
              // Format service center names
              const serviceCenterText = "Service Center: ";

              if (
                !serviceCenter ||
                (Array.isArray(serviceCenter) && serviceCenter.length === 0)
              ) {
                return serviceCenterText + "Loading...";
              }

              // If serviceCenter is an array, join the names
              const serviceCenterNames = Array.isArray(serviceCenter)
                ? serviceCenter
                    .map((sc) => {
                      // Handle different possible formats of service center data
                      if (typeof sc === "object" && sc !== null) {
                        return (
                          sc.name || sc.serviceCenterName || JSON.stringify(sc)
                        );
                      }
                      return String(sc || "");
                    })
                    .filter(Boolean)
                : [
                    typeof serviceCenter === "object" && serviceCenter !== null
                      ? serviceCenter.name ||
                        serviceCenter.serviceCenterName ||
                        JSON.stringify(serviceCenter)
                      : String(serviceCenter || ""),
                  ].filter(Boolean);

              // Join the service center names with a separator
              // const fullText = serviceCenterNames.join(" | ");
              const fullText = serviceCenterNames.join(" | ");

              // Check if the text is too long
              if (fullText.length > 100) {
                // Create truncated text
                let truncatedText = "";
                let currentLength = 0;
                let displayedCenters = 0;

                for (const name of serviceCenterNames) {
                  if (currentLength + name.length + 3 > 97) {
                    // 3 for " | " and leaving room for "..."
                    break;
                  }

                  if (displayedCenters > 0) {
                    truncatedText += " | ";
                    currentLength += 3;
                  }

                  truncatedText += name;
                  currentLength += name.length;
                  displayedCenters++;
                }

                truncatedText += "...";

                // Return with tooltip
                const displayText = serviceCenterText + truncatedText;

                // Format the tooltip to show service centers as a list
                const tooltipText = serviceCenterNames
                  .map((name, index) =>
                    index === serviceCenterNames.length - 1
                      ? name
                      : `${name} | `
                  )
                  .join("");

                return (
                  <Tooltip title={tooltipText}>
                    <span>{displayText}</span>
                  </Tooltip>
                );
              }

              // If not too long, return the full text
              return (
                serviceCenterText +
                fullText +
                (record?.salesOrderNumber
                  ? ` | Sales Order: ${record.salesOrderNumber}`
                  : "")
              );
            })()
      }
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      destroyOnClose
      className={styles["inventory-modal"]}
    >
      <Table
        dataSource={data}
        rowKey="id"
        pagination={false}
        loading={loading}
        onChange={handleTableChange}
        scroll={{ y: 300 }}
        onScroll={handleScroll}
        className={styles["inventory-modal__table"]}
      >
        <Column
          title="Service Center"
          dataIndex="serviceCenter"
          key="serviceCenter"
          width="30%"
        />
        <Column
          title="Item"
          dataIndex="itemNumber"
          key="itemNumber"
          width="30%"
        />
        <Column
          title="Arrival Date"
          dataIndex="arrivalDate"
          key="arrivalDate"
          width="30%"
          sorter
          sortDirections={["ascend", "descend"]}
          render={(d) => (d ? moment(d).format("MM/DD/YYYY") : "-")}
        />
        <Column
          title="Status"
          dataIndex="status"
          key="status"
          width="25%"
          render={(status: string) => (
            <Tag color={getStatusColor(status)}>
              {status
                .toLowerCase()
                .replace("_", " ")
                .replace(/\b\w/g, (l) => l.toUpperCase())}
            </Tag>
          )}
        />
        <Column
          title="Actions"
          key="actions"
          width="15%"
          render={(_: any, row: any) => {
            const isEditable =
              !isEmployee && (isAdmin || row.status === "IN_TRANSIT");

            return (
              <div className={styles["inventory-modal__actions"]}>
                {isEditable ? (
                  <EditOutlined
                    style={{ fontSize: 20, cursor: "pointer" }}
                    onClick={() => onEdit(row)}
                  />
                ) : (
                  <EditOutlined
                    style={{
                      fontSize: 20,
                      opacity: 0.3,
                      cursor: "not-allowed",
                    }}
                  />
                )}
              </div>
            );
          }}
        />
      </Table>
      <div className={styles["inventory-modal__footer"]}>
        <Button onClick={onClose} size="large" className="cancel-button">
          Cancel
        </Button>
      </div>
    </Modal>
  );
};

export default InventoryDetailsModal;
